'use server'

import { apiRequest, ApiResponse } from '../request'

export type EvolutionStatus =
    | 'EVOL_DRAFT'
    | 'EVOL_COMPLETED'
    | 'EVOL_SIGNED'
    | 'EVOL_AMENDED'

export interface NursingEvolution {
    id: number
    companyId: number
    hospitalId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
        bed?: {
            id: number
            number: string
        }
        unit?: {
            id: number
            name: string
        }
    }
    evolutionDate: string
    shift?: string
    status: EvolutionStatus
    subjectiveData?: string
    objectiveData?: string
    assessment?: string
    interventions?: string
    evaluation?: string
    generalCondition?: string
    neurologicalStatus?: string
    respiratoryStatus?: string
    cardiovascularStatus?: string
    gastrointestinalStatus?: string
    genitourinaryStatus?: string
    skinStatus?: string
    mobilityStatus?: string
    painAssessment?: string
    psychosocialStatus?: string
    nursingDiagnoses?: string[]
    nursingInterventions?: string[]
    nursingOutcomes?: string[]
    pendingIssues?: string
    nurseId?: number
    nurse?: {
        id: number
        name: string
    }
    supervisorId?: number
    supervisor?: {
        id: number
        name: string
    }
    signedAt?: string
    signatureHash?: string
    amendmentOf?: number
    amendmentReason?: string
    createdAt: string
    updatedAt: string
}

export interface CreateEvolutionData {
    admissionId: number
    evolutionDate?: string
    shift?: string
    subjectiveData?: string
    objectiveData?: string
    assessment?: string
    interventions?: string
    evaluation?: string
    generalCondition?: string
    neurologicalStatus?: string
    respiratoryStatus?: string
    cardiovascularStatus?: string
    gastrointestinalStatus?: string
    genitourinaryStatus?: string
    skinStatus?: string
    mobilityStatus?: string
    painAssessment?: string
    psychosocialStatus?: string
    nursingDiagnoses?: string[]
    nursingInterventions?: string[]
    nursingOutcomes?: string[]
    pendingIssues?: string
}

export interface UpdateEvolutionData extends Partial<CreateEvolutionData> {}

export interface EvolutionFilters {
    hospitalId?: number
    admissionId?: number
    startDate?: string
    endDate?: string
    status?: EvolutionStatus
}

export async function getEvolutions(
    companyId?: number,
    filters?: EvolutionFilters
): Promise<ApiResponse<NursingEvolution[]>> {
    return apiRequest<NursingEvolution[]>('hospital/nursing-evolution', {
        params: { companyId, ...filters },
    })
}

export async function getEvolution(
    id: number
): Promise<ApiResponse<NursingEvolution>> {
    return apiRequest<NursingEvolution>(`hospital/nursing-evolution/${id}`, {})
}

export async function getAdmissionEvolutions(
    admissionId: number
): Promise<ApiResponse<NursingEvolution[]>> {
    return apiRequest<NursingEvolution[]>(
        `hospital/nursing-evolution/admission/${admissionId}`,
        {}
    )
}

export async function getTodayEvolutions(
    companyId: number
): Promise<ApiResponse<NursingEvolution[]>> {
    return apiRequest<NursingEvolution[]>(
        `hospital/nursing-evolution/today/${companyId}`,
        {}
    )
}

export async function createEvolution(
    data: CreateEvolutionData
): Promise<ApiResponse<NursingEvolution>> {
    return apiRequest<NursingEvolution>(
        'hospital/nursing-evolution',
        { data },
        'post'
    )
}

export async function updateEvolution(
    id: number,
    data: UpdateEvolutionData
): Promise<ApiResponse<NursingEvolution>> {
    return apiRequest<NursingEvolution>(
        `hospital/nursing-evolution/${id}`,
        { data },
        'patch'
    )
}

export async function signEvolution(
    id: number
): Promise<ApiResponse<NursingEvolution>> {
    return apiRequest<NursingEvolution>(
        `hospital/nursing-evolution/${id}/sign`,
        {},
        'post'
    )
}

export async function amendEvolution(
    id: number,
    data: CreateEvolutionData & { amendmentReason: string }
): Promise<ApiResponse<NursingEvolution>> {
    return apiRequest<NursingEvolution>(
        `hospital/nursing-evolution/${id}/amend`,
        { data },
        'post'
    )
}

export async function deleteEvolution(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/nursing-evolution/${id}`, {}, 'delete')
}

export const EVOLUTION_STATUS_LABELS: Record<EvolutionStatus, string> = {
    EVOL_DRAFT: 'Rascunho',
    EVOL_COMPLETED: 'Completa',
    EVOL_SIGNED: 'Assinada',
    EVOL_AMENDED: 'Retificada',
}

export const EVOLUTION_STATUS_COLORS: Record<EvolutionStatus, string> = {
    EVOL_DRAFT: 'badge-warning',
    EVOL_COMPLETED: 'badge-info',
    EVOL_SIGNED: 'badge-success',
    EVOL_AMENDED: 'badge-secondary',
}

export const SHIFT_LABELS: Record<string, string> = {
    MORNING: 'Manhã',
    AFTERNOON: 'Tarde',
    NIGHT: 'Noite',
}
